import { useEffect, useState } from 'react';
import { CpmButton, CpmModal } from '../../../../components/design-system';
import { volumeUnitShortLabel } from '../../../../constants/centralProductMaster.constants';
import { getVolumeSettingUsage } from '../../../../services/productMasterService';
import type { CpmVolumeSetting } from '../../../../types/centralProductMaster.types';

type DelPhase =
    | 'checking'
    | 'checkError'
    | 'blocked'
    | 'allowed'
    | 'deleting'
    | 'disabling'
    | 'saveError';

interface DeleteVolumeConfirmProps {
    open: boolean;
    setting: CpmVolumeSetting | null;
    onClose: () => void;
    onConfirmDelete: () => Promise<string | null>;
    onDisableInstead: (usageCount: number) => Promise<string | null>;
}

export default function DeleteVolumeConfirm({
    open,
    setting,
    onClose,
    onConfirmDelete,
    onDisableInstead,
}: DeleteVolumeConfirmProps) {
    const [phase, setPhase] = useState<DelPhase>('checking');
    const [usage, setUsage] = useState(0);
    const [saveError, setSaveError] = useState('');
    const [wasBlocked, setWasBlocked] = useState(false);

    const runCheck = async (id: string) => {
        setPhase('checking');
        setSaveError('');
        try {
            const data = await getVolumeSettingUsage({ id });
            const count = data.usageCount ?? 0;
            setUsage(count);
            if (count > 0) {
                setWasBlocked(true);
                setPhase('blocked');
            } else {
                setWasBlocked(false);
                setPhase('allowed');
            }
        } catch {
            setPhase('checkError');
        }
    };

    useEffect(() => {
        if (!open || !setting) return;
        void runCheck(setting._id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, setting?._id]);

    if (!setting) return null;

    const label = `${Number(setting.value).toLocaleString('en-IN')} ${volumeUnitShortLabel(setting.unit)}`;
    const busy = phase === 'deleting' || phase === 'disabling';
    const showBlocked =
        phase === 'blocked' || phase === 'disabling' || (phase === 'saveError' && wasBlocked);
    const showAllowed =
        phase === 'allowed' || phase === 'deleting' || (phase === 'saveError' && !wasBlocked);
    // showBlocked/showAllowed already cover 'deleting' | 'disabling' | 'saveError'.
    const resolved = showBlocked || showAllowed;

    const handleDelete = async () => {
        if (busy) return;
        setPhase('deleting');
        setSaveError('');
        const err = await onConfirmDelete();
        if (err) {
            setSaveError(err);
            setPhase('saveError');
            setWasBlocked(false);
            return;
        }
    };

    const handleDisable = async () => {
        if (busy) return;
        setPhase('disabling');
        setSaveError('');
        const err = await onDisableInstead(usage);
        if (err) {
            setSaveError(err);
            setPhase('saveError');
            setWasBlocked(true);
            return;
        }
    };

    return (
        <CpmModal
            open={open}
            onClose={busy ? () => undefined : onClose}
            maxWidth={540}
            zIndex={72}
        >
            {phase === 'checking' ? (
                <div className="flex flex-col" style={{ gap: 14, padding: 24 }}>
                    <div className="flex items-center" style={{ gap: 10 }}>
                        <span className="cpm-spin" />
                        <span
                            className="uppercase text-[var(--cpm-text-tertiary)]"
                            style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' }}
                        >
                            Checking where this volume is used
                        </span>
                    </div>
                    <span className="cpm-sk" style={{ height: 20, width: '62%' }} />
                    <span className="cpm-sk" style={{ height: 13, width: '88%' }} />
                    <span className="cpm-sk" style={{ height: 13, width: '70%' }} />
                </div>
            ) : null}

            {phase === 'checkError' ? (
                <div className="flex items-start" style={{ gap: 14, padding: '22px 24px 16px' }}>
                    <span
                        className="shrink-0 inline-flex items-center justify-center text-[var(--cpm-shortfall-text)]"
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: 'var(--cpm-shortfall-bg)',
                        }}
                    >
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                            <path d="M12 9v4" />
                            <path d="M12 17h.01" />
                        </svg>
                    </span>
                    <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                        <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                            Couldn't check usage
                        </h2>
                        <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                            We couldn't confirm whether any variant still uses {label}, so the
                            delete is held back. Nothing has been removed.
                        </p>
                    </div>
                </div>
            ) : null}

            {resolved ? (
                <>
                    <div className="flex items-start" style={{ gap: 14, padding: '22px 24px 16px' }}>
                        <span
                            className="shrink-0 inline-flex items-center justify-center"
                            style={{
                                width: 38,
                                height: 38,
                                borderRadius: 10,
                                background: showBlocked
                                    ? 'var(--cpm-warning-bg)'
                                    : 'var(--cpm-shortfall-bg)',
                                color: showBlocked
                                    ? 'var(--cpm-warning-text)'
                                    : 'var(--cpm-shortfall-text)',
                            }}
                        >
                            {showBlocked ? (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                                    <path d="M12 9v4" />
                                    <path d="M12 17h.01" />
                                </svg>
                            ) : (
                                <svg
                                    width="19"
                                    height="19"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M3 6h18" />
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                    <path d="M10 11v6" />
                                    <path d="M14 11v6" />
                                </svg>
                            )}
                        </span>
                        <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                            <h2 className="m-0 !text-[19px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                                {showBlocked ? `Cannot delete ${label}` : `Delete ${label}?`}
                            </h2>
                            <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                                {showBlocked
                                    ? 'Variants are still saved at this size, so removing the option outright would leave them pointing at a volume that no longer exists in settings.'
                                    : 'Nothing is saved at this size. This only removes the option from future creates; existing variants keep their volume unchanged.'}
                            </p>
                        </div>
                    </div>
                    {showBlocked ? (
                        <div
                            className="flex flex-col"
                            style={{ margin: '0 24px 4px', gap: 8 }}
                        >
                            <span
                                className="uppercase text-[var(--cpm-text-tertiary)]"
                                style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                            >
                                Still in use
                            </span>
                            <div
                                className="flex items-center rounded-[10px] border border-[var(--cpm-warning-border)] bg-[var(--cpm-warning-bg)]"
                                style={{ gap: 12, padding: '11px 14px' }}
                            >
                                <span
                                    className="tabular-nums text-[var(--cpm-warning-text)]"
                                    style={{ fontSize: 16, fontWeight: 800, minWidth: 56 }}
                                >
                                    {usage.toLocaleString('en-IN')}
                                </span>
                                <span
                                    className="flex-1 text-[var(--cpm-warning-text)]"
                                    style={{ fontSize: 12.5, fontWeight: 700 }}
                                >
                                    variants are saved at {label}
                                </span>
                            </div>
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    lineHeight: 1.5,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                Disable it instead — those variants keep their volume, and the size
                                stops being offered on create.
                            </span>
                        </div>
                    ) : null}
                </>
            ) : null}

            {phase === 'saveError' && saveError ? (
                <div
                    className="flex items-start rounded-[10px] border border-[var(--cpm-shortfall-border)] bg-[var(--cpm-shortfall-bg)]"
                    style={{ margin: '14px 24px 0', gap: 9, padding: '11px 13px' }}
                >
                    <svg
                        width="15"
                        height="15"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--cpm-shortfall-text)"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="shrink-0"
                        style={{ marginTop: 1 }}
                    >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 8v4" />
                        <path d="M12 16h.01" />
                    </svg>
                    <span
                        className="text-[var(--cpm-shortfall-text)]"
                        style={{ fontSize: 12.5, fontWeight: 700, lineHeight: 1.45 }}
                    >
                        {saveError}
                    </span>
                </div>
            ) : null}

            <div
                className="flex items-center border-t-[1.5px] border-[var(--cpm-border-strong)]"
                style={{
                    gap: 10,
                    padding: '18px 24px',
                    marginTop: 14,
                    background: 'rgba(249,249,252,0.88)',
                }}
            >
                <div className="ml-auto flex items-center" style={{ gap: 10 }}>
                    {phase !== 'checking' ? (
                        <CpmButton
                            variant="outline"
                            size="modal"
                            onClick={onClose}
                            disabled={busy}
                        >
                            {showBlocked ? 'Back to the list' : 'Cancel'}
                        </CpmButton>
                    ) : null}
                    {phase === 'checkError' ? (
                        <CpmButton
                            variant="navy"
                            size="modal"
                            leadingIcon={
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                    <path d="M3 3v5h5" />
                                </svg>
                            }
                            onClick={() => void runCheck(setting._id)}
                        >
                            Check again
                        </CpmButton>
                    ) : null}
                    {showBlocked ? (
                        <CpmButton
                            variant="warn"
                            size="modal"
                            onClick={() => void handleDisable()}
                            isLoading={phase === 'disabling'}
                            className="!bg-[var(--cpm-warning-text)] !text-white !border-0 !shadow-[0_8px_20px_-6px_rgba(217,119,6,.45)]"
                        >
                            {phase === 'disabling' ? 'Disabling' : 'Disable instead'}
                        </CpmButton>
                    ) : null}
                    {showAllowed ? (
                        <CpmButton
                            variant="danger"
                            size="modal"
                            onClick={() => void handleDelete()}
                            isLoading={phase === 'deleting'}
                            className="!bg-[var(--cpm-shortfall-text)] !shadow-[0_8px_20px_-6px_rgba(212,24,61,0.5)]"
                        >
                            {phase === 'deleting' ? 'Deleting' : 'Delete volume'}
                        </CpmButton>
                    ) : null}
                </div>
            </div>
        </CpmModal>
    );
}
